import { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { deleteUser as deleteUserApi, resetUserPassword } from '../../../services/users';

const UsersActionsCell = ({ user, clinic }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [resetStatus, setResetStatus] = useState('idle');

    const open = Boolean(anchorEl);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const goEditUsers = () => {
        handleClose();

        navigate('/mgmt/clinic/users/edit', {
            state: {
                user,
                clinic
            }
        });
    };

    const handleDeleteClick = () => {
        handleClose();
        setOpenDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            await deleteUserApi(user.role_uuid);
            window.location.reload();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setOpenDialog(false);
        }
    };

    const handleResetPassword = async () => {
        try {
            setResetLoading(true);
            await resetUserPassword(user.role_uuid);

            setResetStatus('success');
        } catch (err) {
            console.error(err);
            setResetStatus('error');
        } finally {
            setResetLoading(false);
        }
    };

    const handleCloseResetDialog = () => {
        setOpenResetDialog(false);
        setResetStatus('idle');
    };

    return (
        <>
            <IconButton size="small" onClick={handleOpen}>
                <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={goEditUsers}>{t('clinics.editUser')}</MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setOpenResetDialog(true);
                    }}
                >
                    {t('users.resetPassword')}
                </MenuItem>
                <MenuItem onClick={handleDeleteClick}>{t('clinics.delete')}</MenuItem>
            </Menu>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>{t('users.confirmDeleteTitle')}</DialogTitle>

                <DialogContent>
                    <Typography>
                        {t('users.confirmDeleteMessage', {
                            name: user.name
                        })}
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>
                        {t('common.cancel')}
                    </Button>

                    <Button
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={loading}
                    >
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openResetDialog} onClose={handleCloseResetDialog}>
                <DialogTitle>
                    {resetStatus === 'idle' && t('users.confirmResetPasswordTitle')}
                    {resetStatus === 'success' && t('users.resetPasswordSuccessTitle')}
                    {resetStatus === 'error' && t('users.resetPasswordErrorTitle')}
                </DialogTitle>

                <DialogContent>
                    {resetStatus === 'idle' && (
                        <Typography>
                            {t('users.confirmResetPasswordMessage', {
                                name: user.name
                            })}
                        </Typography>
                    )}

                    {resetStatus === 'success' && (
                        <Typography>{t('users.passwordResetSuccess')}</Typography>
                    )}

                    {resetStatus === 'error' && (
                        <Typography>{t('users.passwordResetError')}</Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    {resetStatus === 'idle' ? (
                        <>
                            <Button onClick={handleCloseResetDialog}>
                                {t('common.cancel')}
                            </Button>

                            <Button onClick={handleResetPassword} disabled={resetLoading}>
                                {t('common.confirm')}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={handleCloseResetDialog}>Ok</Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UsersActionsCell;
